import { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  Users, Shield, Flag, BarChart3, Search, ChevronLeft, ChevronRight, Check, X, Ban, Trash2, Loader2,
  Heart, Image, CreditCard, Globe, Smartphone, Monitor, Eye, Crown, UserCheck, Lock,
  FileText, Settings as SettingsIcon, DollarSign, TrendingUp, Clock, AlertTriangle,
  MessageSquare, ThumbsUp, Save, RefreshCw, MailCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import SEOHead from '../components/SEOHead';
import API_URL from '../config/api';

const CC = { PT: 'Portugal', FR: 'France', CH: 'Switzerland', LU: 'Luxembourg', BE: 'Belgium', DE: 'Germany', ES: 'Spain' };
const PC = { free: 'bg-[#E5E3DB] text-[#6C6C67]', premium: 'bg-[#6E1A24] text-white', gold: 'bg-[#D4AF37] text-white' };

const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'verifications', label: 'Verifications', icon: Shield },
  { key: 'photos', label: 'Photos', icon: Image },
  { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  { key: 'payments', label: 'Payments', icon: DollarSign },
  { key: 'reports', label: 'Reports', icon: Flag },
  { key: 'admin-actions', label: 'Action Logs', icon: FileText },
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

/* Shared */
const Spin = () => <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#6E1A24] animate-spin" /></div>;
const Empty = ({ icon: Icon, text }) => <div className="bg-white rounded-2xl border border-[#E5E3DB] p-12 text-center"><Icon className="w-10 h-10 text-[#9C9C97] mx-auto mb-3" /><p className="text-[#6C6C67]">{text}</p></div>;
const Pag = ({ page, pages, set }) => pages > 1 ? (
  <div className="flex items-center justify-center gap-3 p-4 border-t border-[#E5E3DB]">
    <button onClick={() => set(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-2 rounded-lg hover:bg-[#F5F2EB] disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
    <span className="text-xs text-[#6C6C67]">Page {page} of {pages}</span>
    <button onClick={() => set(p => Math.min(pages, p + 1))} disabled={page >= pages} className="p-2 rounded-lg hover:bg-[#F5F2EB] disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
  </div>
) : null;
const FB = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} className={`px-3.5 py-2 rounded-lg text-xs font-medium transition-all ${active ? 'bg-[#6E1A24] text-white' : 'bg-white text-[#6C6C67] border border-[#E5E3DB] hover:bg-[#F5F2EB]'}`} data-testid={testId}>{children}</button>
);

export default function Admin() {
  const { logout } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailUserId, setDetailUserId] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(() => toast.error('Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  const viewUser = (id) => { setDetailUserId(id); setTab('user-detail'); };
  const backToUsers = () => { setDetailUserId(null); setTab('users'); };

  const tabTitle = tab === 'user-detail' ? 'User Detail' : tab === 'admin-actions' ? 'Action Logs' : tab;

  return (
    <div className="min-h-screen bg-[#F5F3EE] flex" data-testid="admin-panel">
      <SEOHead title="Admin" noindex path="/admin" />
      <aside className="w-56 bg-[#2D2D2A] text-white flex-shrink-0 flex flex-col h-screen sticky top-0">
        <div className="p-5 border-b border-white/10">
          <a href="/" className="flex items-center gap-2"><Heart className="w-6 h-6 text-[#C97A63]" fill="currentColor" fillOpacity={0.3} /><span className="text-lg font-serif">LusoLove</span></a>
          <span className="text-[10px] uppercase tracking-widest text-white/40 mt-1 block">Back Office</span>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(n => (
            <button key={n.key} onClick={() => setTab(n.key)} data-testid={`nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-all ${tab === n.key ? 'bg-white/10 text-white border-r-2 border-[#C97A63]' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
              <n.icon className="w-4 h-4" />{n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={logout} className="w-full text-left text-xs text-white/40 hover:text-white/70" data-testid="admin-logout">Sign Out</button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <header className="bg-white border-b border-[#E5E3DB] px-8 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            {tab === 'user-detail' && <button onClick={backToUsers} className="p-1.5 rounded-lg hover:bg-[#F5F2EB]"><ChevronLeft className="w-5 h-5" /></button>}
            <h1 className="text-lg font-serif font-medium text-[#2D2D2A] capitalize" data-testid="admin-page-title">{tabTitle}</h1>
          </div>
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[#2D6A4F] animate-pulse" /><span className="text-xs text-[#6C6C67]">Live</span></div>
        </header>
        <div className="p-6 lg:p-8">
          {tab === 'dashboard' && <DashboardTab stats={stats} loading={loading} />}
          {tab === 'users' && <UsersTab onView={viewUser} />}
          {tab === 'user-detail' && detailUserId && <UserDetailTab userId={detailUserId} onBack={backToUsers} />}
          {tab === 'verifications' && <VerificationsTab />}
          {tab === 'photos' && <PhotosTab />}
          {tab === 'subscriptions' && <SubscriptionsTab />}
          {tab === 'payments' && <PaymentsTab />}
          {tab === 'reports' && <ReportsTab />}
          {tab === 'admin-actions' && <ActionLogsTab />}
          {tab === 'settings' && <SettingsTab />}
        </div>
      </main>
    </div>
  );
}

/* ═══════════ DASHBOARD ═══════════ */
function DashboardTab({ stats, loading }) {
  if (loading) return <Spin />;
  const s = stats || {};
  const chartTick = { fontSize: 10 };
  const barRadius = [4,4,0,0];
  const kpis = [
    { l: 'Total Users', v: s.total_users, i: Users, c: 'text-[#6E1A24]', b: 'bg-[#6E1A24]/8' },
    { l: 'New Today', v: s.new_users_today, i: UserCheck, c: 'text-[#2D6A4F]', b: 'bg-[#2D6A4F]/10', sub: `${s.new_users_7d || 0} this week` },
    { l: 'Premium', v: s.premium_users, i: Crown, c: 'text-[#6E1A24]', b: 'bg-[#6E1A24]/8' },
    { l: 'Gold', v: s.gold_users, i: Crown, c: 'text-[#D4AF37]', b: 'bg-[#D4AF37]/10' },
    { l: 'MRR Estimate', v: `\u20AC${s.mrr_estimate || 0}`, i: DollarSign, c: 'text-[#2D6A4F]', b: 'bg-[#2D6A4F]/10' },
    { l: 'Matches', v: s.total_matches, i: Heart, c: 'text-[#C97A63]', b: 'bg-[#C97A63]/10' },
    { l: 'Messages', v: s.total_messages, i: MessageSquare, c: 'text-blue-600', b: 'bg-blue-50' },
    { l: 'Photos', v: s.total_photos, i: Image, c: 'text-purple-600', b: 'bg-purple-50' },
    { l: 'Verified', v: s.verified_users, i: Shield, c: 'text-[#2D6A4F]', b: 'bg-[#2D6A4F]/10' },
    { l: 'Email Verified', v: s.email_verified_users, i: MailCheck, c: 'text-blue-600', b: 'bg-blue-50' },
    { l: 'Suspended', v: s.suspended_users, i: Ban, c: 'text-red-500', b: 'bg-red-50' },
    { l: 'Pending Reports', v: s.pending_reports, i: Flag, c: 'text-amber-600', b: 'bg-amber-50' },
    { l: 'Pending Verif.', v: s.pending_verifications, i: Clock, c: 'text-[#6C6C67]', b: 'bg-[#F5F2EB]' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {kpis.map(k => (
          <div key={k.l} className="bg-white rounded-2xl border border-[#E5E3DB] p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-7 h-7 rounded-lg ${k.b} flex items-center justify-center`}><k.i className={`w-3.5 h-3.5 ${k.c}`} /></div>
              <span className="text-[10px] uppercase tracking-wider text-[#9C9C97] font-medium leading-tight">{k.l}</span>
            </div>
            <p className="text-xl font-serif font-medium text-[#2D2D2A]" data-testid={`kpi-${k.l.toLowerCase().replace(/[^a-z]/g,'-')}`}>{k.v ?? 0}</p>
            {k.sub && <p className="text-[10px] text-[#9C9C97] mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> User Growth (30d)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={s.user_growth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" /><XAxis dataKey="_id" tick={chartTick} tickFormatter={v => v?.slice(5)} />
              <YAxis tick={chartTick} /><Tooltip /><Bar dataKey="count" fill="#6E1A24" radius={barRadius} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-4 flex items-center gap-2"><Heart className="w-4 h-4" /> Matches (30d)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={s.match_growth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" /><XAxis dataKey="_id" tick={chartTick} tickFormatter={v => v?.slice(5)} />
              <YAxis tick={chartTick} /><Tooltip /><Line type="monotone" dataKey="count" stroke="#C97A63" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-4 flex items-center gap-2"><Globe className="w-4 h-4" /> By Country</h3>
          <div className="space-y-2.5">{(s.users_by_country||[]).map(i => (
            <div key={i._id} className="flex justify-between"><span className="text-sm text-[#2D2D2A]">{CC[i._id]||i._id}</span><span className="text-sm font-medium text-[#6E1A24] bg-[#6E1A24]/8 px-2.5 py-0.5 rounded-full">{i.count}</span></div>
          ))}</div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-4 flex items-center gap-2"><Smartphone className="w-4 h-4" /> By Platform</h3>
          <div className="space-y-2.5">{(s.users_by_platform||[]).map(i => (
            <div key={i._id} className="flex items-center justify-between"><div className="flex items-center gap-2">{i._id==='ios'||i._id==='android'?<Smartphone className="w-4 h-4 text-[#6C6C67]" />:<Monitor className="w-4 h-4 text-[#6C6C67]" />}<span className="text-sm text-[#2D2D2A] capitalize">{i._id||'web'}</span></div><span className="text-sm font-medium">{i.count}</span></div>
          ))}</div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Subscriptions</h3>
          <div className="space-y-2.5">{(s.subscription_breakdown||[]).map(i => {
            const p = i._id||'free';
            return <div key={p} className="flex justify-between"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${PC[p]||PC.free}`}>{p}</span><span className="text-sm font-medium text-[#2D2D2A]">{i.count}</span></div>;
          })}</div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════ USERS ═══════════ */
function UsersTab({ onView }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionUser, setActionUser] = useState(null);
  const [actionType, setActionType] = useState('');
  const [showDialog, setShowDialog] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: 20 });
      if (search) p.append('search', search);
      if (filter) p.append('status', filter);
      if (planFilter) p.append('plan', planFilter);
      const r = await axios.get(`${API_URL}/api/admin/users?${p}`, { withCredentials: true });
      setUsers(r.data.users); setPages(r.data.pages); setTotal(r.data.total);
    } catch (err) { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [page, search, filter, planFilter]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doAction = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/user-action`, { user_id: actionUser.user_id, action: actionType }, { withCredentials: true });
      toast.success(`User ${actionType}${actionType.endsWith('e') ? 'd' : 'ed'}`);
      setShowDialog(false); fetch_();
    } catch (err) { toast.error(`Failed to ${actionType} user`); }
  };

  return (
    <div data-testid="users-tab">
      <div className="bg-white rounded-2xl border border-[#E5E3DB] mb-6">
        <div className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9C97]" />
            <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name, email, or user ID..." className="w-full pl-10 pr-4 py-2.5 border border-[#E5E3DB] rounded-xl text-sm focus:outline-none focus:border-[#6E1A24]" data-testid="users-search" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[{v:'',l:'All'},{v:'verified',l:'Verified'},{v:'unverified',l:'Unverified'},{v:'suspended',l:'Suspended'}].map(f => (
              <FB key={f.v} active={filter===f.v} onClick={() => { setFilter(f.v); setPage(1); }} testId={`filter-${f.v||'all'}`}>{f.l}</FB>
            ))}
            <span className="w-px bg-[#E5E3DB] mx-1" />
            {['all','free','premium','gold'].map(f => (
              <FB key={f} active={planFilter===f || (!planFilter&&f==='all')} onClick={() => { setPlanFilter(f==='all'?'':f); setPage(1); }}>{f === 'all' ? 'All Plans' : f.charAt(0).toUpperCase()+f.slice(1)}</FB>
            ))}
          </div>
        </div>
        <p className="px-4 pb-3 text-xs text-[#9C9C97]">{total} users total</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
        {loading ? <Spin /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E5E3DB] bg-[#FAF9F6]">
                {['User','Location','Platform','Plan','Status','Actions'].map(h => (
                  <th key={h} className={`${h==='Actions'?'text-right':'text-left'} px-5 py-3 text-[10px] uppercase tracking-wider text-[#9C9C97] font-semibold`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{users.map(u => {
                const plan = u.subscription?.plan || 'free';
                return (
                  <tr key={u.user_id} className="border-b border-[#E5E3DB]/50 hover:bg-[#FAF9F6] transition-colors cursor-pointer" data-testid={`user-row-${u.user_id}`} onClick={() => onView(u.user_id)}>
                    <td className="px-5 py-3"><p className="font-medium text-[#2D2D2A]">{u.nickname||u.name||'N/A'}</p><p className="text-xs text-[#9C9C97]">{u.email}</p></td>
                    <td className="px-5 py-3 text-[#6C6C67] text-xs">{u.city}{u.country ? `, ${CC[u.country]||u.country}` : ''}</td>
                    <td className="px-5 py-3"><span className="text-xs bg-[#F5F2EB] text-[#6C6C67] px-2 py-0.5 rounded capitalize">{u.signup_platform||'web'}</span></td>
                    <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${PC[plan]}`}>{plan}</span></td>
                    <td className="px-5 py-3">{u.is_suspended ? <span className="text-xs text-red-500 font-medium">Suspended</span> : u.is_verified ? <span className="text-xs text-[#2D6A4F]">Verified</span> : <span className="text-xs text-[#9C9C97]">Active</span>}</td>
                    <td className="px-5 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => onView(u.user_id)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500" title="View"><Eye className="w-4 h-4" /></button>
                        {!u.is_verified && <button onClick={() => { setActionUser(u); setActionType('verify'); setShowDialog(true); }} className="p-1.5 rounded-lg hover:bg-[#2D6A4F]/10 text-[#2D6A4F]" title="Verify"><Check className="w-4 h-4" /></button>}
                        {!u.is_suspended ? <button onClick={() => { setActionUser(u); setActionType('suspend'); setShowDialog(true); }} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Suspend"><Ban className="w-4 h-4" /></button>
                          : <button onClick={() => { setActionUser(u); setActionType('unsuspend'); setShowDialog(true); }} className="p-1.5 rounded-lg hover:bg-[#2D6A4F]/10 text-[#2D6A4F]" title="Unsuspend"><Check className="w-4 h-4" /></button>}
                        <button onClick={() => { setActionUser(u); setActionType('delete'); setShowDialog(true); }} className="p-1.5 rounded-lg hover:bg-red-50 text-red-300" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>);
              })}</tbody>
            </table>
          </div>
        )}
        <Pag page={page} pages={pages} set={setPage} />
      </div>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent><AlertDialogHeader>
          <AlertDialogTitle className="capitalize">{actionType} User</AlertDialogTitle>
          <AlertDialogDescription>{actionType==='delete'?'Permanently delete user and all data.':actionType==='suspend'?'Suspend user from the platform.':actionType==='verify'?'Mark user as verified.':'Restore user access.'}<br /><strong>{actionUser?.name||actionUser?.email}</strong></AlertDialogDescription>
        </AlertDialogHeader><AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={doAction} className={actionType==='delete'||actionType==='suspend'?'bg-red-500 hover:bg-red-600':'bg-[#2D6A4F] hover:bg-[#245A42]'}>{actionType}</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ═══════════ USER DETAIL ═══════════ */
function UserDetailTab({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [subPlan, setSubPlan] = useState('');
  const [showSubDialog, setShowSubDialog] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true })
      .then(r => { setData(r.data); setSubPlan(r.data.user?.subscription?.plan || 'free'); })
      .catch(() => toast.error('Failed to load user'))
      .finally(() => setLoading(false));
  }, [userId]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/note`, { note: noteText }, { withCredentials: true });
      toast.success('Note added');
      setNoteText('');
      const r = await axios.get(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      setData(r.data);
    } catch (err) { toast.error('Failed to add note'); }
    finally { setSavingNote(false); }
  };

  const updateSub = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/subscription`, { plan: subPlan }, { withCredentials: true });
      toast.success(`Subscription updated to ${subPlan}`);
      setShowSubDialog(false);
      const r = await axios.get(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      setData(r.data);
    } catch (err) { toast.error('Failed to update subscription'); }
  };

  if (loading) return <Spin />;
  if (!data) return <Empty icon={Users} text="User not found" />;

  const u = data.user;
  const plan = u.subscription?.plan || 'free';
  const S = ({ l, v }) => v ? <div className="flex justify-between py-1.5 border-b border-[#E5E3DB]/50 last:border-0"><span className="text-xs text-[#9C9C97]">{l}</span><span className="text-xs text-[#2D2D2A] font-medium text-right max-w-[60%] truncate">{v}</span></div> : null;

  return (
    <div className="space-y-6" data-testid="user-detail-tab">
      {/* Header card */}
      <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6">
        <div className="flex items-start gap-5">
          <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#F5F2EB] flex-shrink-0">
            {data.photos?.length > 0 ? <img src={`${API_URL}/api/photos/${data.photos[0].photo_id}?v=1`} alt="" className="w-full h-full object-cover" /> : <Users className="w-8 h-8 text-[#9C9C97] m-auto mt-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-serif font-medium text-[#2D2D2A]" data-testid="detail-user-name">{u.nickname || u.name || 'N/A'}</h2>
            <p className="text-sm text-[#6C6C67]">{u.email}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase ${PC[plan]}`}>{plan}</span>
              {u.is_verified && <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F]">Verified</span>}
              {u.is_suspended && <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-600">Suspended</span>}
              <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#F5F2EB] text-[#6C6C67] capitalize">{u.signup_platform || 'web'}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#9C9C97]">Profile Completion</p>
            <p className="text-2xl font-serif font-medium text-[#6E1A24]">{data.profile_completion}%</p>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { l: 'Matches', v: data.matches_count, i: Heart, c: 'text-[#C97A63]' },
          { l: 'Messages Sent', v: data.messages_count, i: MessageSquare, c: 'text-blue-600' },
          { l: 'Likes Given', v: data.likes_given, i: ThumbsUp, c: 'text-[#6E1A24]' },
          { l: 'Likes Received', v: data.likes_received, i: ThumbsUp, c: 'text-[#D4AF37]' },
        ].map(k => (
          <div key={k.l} className="bg-white rounded-2xl border border-[#E5E3DB] p-4 text-center">
            <k.i className={`w-5 h-5 ${k.c} mx-auto mb-1`} />
            <p className="text-lg font-serif font-medium text-[#2D2D2A]">{k.v ?? 0}</p>
            <p className="text-[10px] text-[#9C9C97]">{k.l}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Account Info */}
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">Account</h3>
          <S l="User ID" v={u.user_id} /><S l="Email" v={u.email} /><S l="Auth Provider" v={u.auth_provider || 'email'} />
          <S l="Role" v={u.role || 'user'} /><S l="Created" v={u.created_at?.split('T')[0]} />
          <S l="Signup Platform" v={u.signup_platform || 'web'} /><S l="Language" v={u.language} />
        </div>
        {/* Profile Info */}
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">Profile</h3>
          <S l="Name" v={u.name} /><S l="Nickname" v={u.nickname} /><S l="Birthdate" v={u.birthdate} />
          <S l="Gender" v={u.gender} /><S l="City" v={u.city} /><S l="Country" v={CC[u.country]||u.country} />
          <S l="Bio" v={u.bio?.substring(0, 80)} /><S l="Looking For" v={u.looking_for} />
          <S l="Intention" v={u.relationship_intention} /><S l="Languages" v={u.spoken_languages?.join(', ')} />
        </div>
      </div>

      {/* Photos */}
      {data.photos?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">Photos ({data.photos.length})</h3>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {data.photos.map((p, i) => (
              <div key={p.photo_id} className="relative aspect-[3/4] rounded-xl overflow-hidden bg-[#F5F2EB]">
                <img src={`${API_URL}/api/photos/${p.photo_id}?v=1`} alt="" className="w-full h-full object-cover" />
                {p.is_primary && <div className="absolute top-1 left-1 bg-[#6E1A24] text-white text-[8px] font-bold uppercase px-1.5 py-0.5 rounded">Main</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Subscription Management */}
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">Subscription</h3>
          <S l="Current Plan" v={plan} /><S l="Status" v={u.subscription?.subscription_status || 'N/A'} />
          <S l="Provider" v={u.subscription?.provider || 'N/A'} />
          <S l="Expires" v={u.subscription?.expires_at?.split('T')[0]} />
          <S l="Subscription ID" v={u.subscription?.provider_subscription_id || 'N/A'} />
          <S l="Customer ID" v={u.subscription?.provider_customer_id || 'N/A'} />
          <div className="mt-4 flex gap-2">
            <select value={subPlan} onChange={e => setSubPlan(e.target.value)} className="flex-1 px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm">
              <option value="free">Free</option><option value="premium">Premium</option><option value="gold">Gold</option>
            </select>
            <Button onClick={() => setShowSubDialog(true)} className="bg-[#6E1A24] text-white text-xs rounded-lg px-4" data-testid="update-subscription-btn">Update</Button>
          </div>
        </div>
        {/* Reports */}
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">Reports Received ({data.reports_received?.length || 0})</h3>
          {data.reports_received?.length > 0 ? data.reports_received.slice(0, 5).map(r => (
            <div key={r.report_id} className="py-2 border-b border-[#E5E3DB]/50 last:border-0">
              <p className="text-xs text-[#2D2D2A] font-medium">{r.reason}</p>
              <p className="text-[10px] text-[#9C9C97]">{r.status} - {r.created_at?.split('T')[0]}</p>
            </div>
          )) : <p className="text-xs text-[#9C9C97] italic">No reports</p>}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-2xl border border-[#E5E3DB] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">Admin Notes</h3>
        <div className="flex gap-2 mb-4">
          <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add an internal note..."
            className="flex-1 px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm focus:outline-none focus:border-[#6E1A24]" data-testid="admin-note-input" />
          <Button onClick={addNote} disabled={savingNote || !noteText.trim()} className="bg-[#6E1A24] text-white text-xs rounded-lg px-4" data-testid="admin-note-submit">
            {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
          </Button>
        </div>
        {data.notes?.length > 0 ? data.notes.map(n => (
          <div key={n.note_id} className="py-2 border-b border-[#E5E3DB]/50 last:border-0">
            <p className="text-xs text-[#2D2D2A]">{n.note}</p>
            <p className="text-[10px] text-[#9C9C97]">by {n.admin_email} - {n.created_at?.split('T')[0]}</p>
          </div>
        )) : <p className="text-xs text-[#9C9C97] italic">No notes yet</p>}
      </div>

      {/* Admin Actions History */}
      {data.admin_actions?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E3DB] p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">Admin Action History</h3>
          {data.admin_actions.map(a => (
            <div key={a.log_id} className="py-2 border-b border-[#E5E3DB]/50 last:border-0">
              <p className="text-xs text-[#2D2D2A] font-medium capitalize">{a.action_type.replace(/_/g, ' ')}</p>
              <p className="text-[10px] text-[#9C9C97]">by {a.admin_email} - {a.created_at?.split('T')[0]} {a.note && `- ${a.note}`}</p>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={showSubDialog} onOpenChange={setShowSubDialog}>
        <AlertDialogContent><AlertDialogHeader>
          <AlertDialogTitle>Update Subscription</AlertDialogTitle>
          <AlertDialogDescription>Change {u.name || u.email}'s plan to <strong className="capitalize">{subPlan}</strong>?</AlertDialogDescription>
        </AlertDialogHeader><AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={updateSub} className="bg-[#6E1A24] hover:bg-[#8A2331]">Confirm</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ═══════════ VERIFICATIONS ═══════════ */
function VerificationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/api/admin/verifications?status=${filter}`, { withCredentials: true }); setItems(r.data.verifications); }
    catch (err) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { fetch_(); }, [fetch_]);

  const handleAction = async (uid, action) => {
    try { await axios.post(`${API_URL}/api/admin/verifications/${uid}?action=${action}`, {}, { withCredentials: true }); toast.success(action === 'approve' ? 'Verified' : 'Rejected'); fetch_(); }
    catch (err) { toast.error('Action failed'); }
  };

  return (
    <div data-testid="verifications-tab">
      <div className="flex gap-1.5 mb-6">{['all','verified','pending','unverified'].map(f => <FB key={f} active={filter===f} onClick={() => setFilter(f)} testId={`verif-filter-${f}`}><span className="capitalize">{f}</span></FB>)}</div>
      {loading ? <Spin /> : items.length === 0 ? <Empty icon={Shield} text="No verifications" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(u => (
            <div key={u.user_id} className="bg-white rounded-2xl border border-[#E5E3DB] p-4" data-testid={`verif-card-${u.user_id}`}>
              <div className="flex items-center gap-3 mb-3">
                {u.photos?.length > 0 ? <img src={`${API_URL}/api/photos/${u.photos[0]}?v=1`} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <div className="w-12 h-12 rounded-xl bg-[#F5F2EB] flex items-center justify-center"><Users className="w-5 h-5 text-[#9C9C97]" /></div>}
                <div><p className="font-medium text-sm text-[#2D2D2A]">{u.nickname||u.name}</p><p className="text-xs text-[#9C9C97]">{u.email}</p></div>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${u.is_verified ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#F5F2EB] text-[#9C9C97]'}`}>{u.is_verified ? 'Verified' : 'Unverified'}</span>
                {!u.is_verified && <div className="flex gap-1.5">
                  <button onClick={() => handleAction(u.user_id, 'approve')} className="p-2 rounded-lg bg-[#2D6A4F]/10 text-[#2D6A4F] hover:bg-[#2D6A4F]/20" data-testid={`verif-approve-${u.user_id}`}><Check className="w-4 h-4" /></button>
                  <button onClick={() => handleAction(u.user_id, 'reject')} className="p-2 rounded-lg bg-red-50 text-red-400 hover:bg-red-100" data-testid={`verif-reject-${u.user_id}`}><X className="w-4 h-4" /></button>
                </div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════ PHOTOS ═══════════ */
function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/api/admin/photos?page=${page}&limit=24`, { withCredentials: true }); setPhotos(r.data.photos); setPages(r.data.pages); }
    catch (err) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [page]);
  useEffect(() => { fetch_(); }, [fetch_]);

  const del = async (id) => {
    try { await axios.delete(`${API_URL}/api/admin/photos/${id}`, { withCredentials: true }); toast.success('Deleted'); fetch_(); }
    catch (err) { toast.error('Failed'); }
  };

  return (
    <div data-testid="photos-tab">
      {loading ? <Spin /> : photos.length === 0 ? <Empty icon={Image} text="No photos" /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {photos.map(p => (
              <div key={p.photo_id} className="group relative aspect-[3/4] rounded-xl overflow-hidden bg-[#F5F2EB]" data-testid={`photo-${p.photo_id}`}>
                <img src={`${API_URL}/api/photos/${p.photo_id}?v=1`} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-medium truncate">{p.user?.name||'Unknown'}</p>
                    <div className="flex justify-between mt-1"><span className="text-white/60 text-[10px]">{p.user?.email?.split('@')[0]}</span>
                      <button onClick={() => del(p.photo_id)} className="p-1 rounded bg-red-500/80 text-white hover:bg-red-600" data-testid={`photo-delete-${p.photo_id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
                {p.is_primary && <div className="absolute top-1.5 left-1.5 bg-[#6E1A24] text-white text-[8px] font-bold uppercase px-1.5 py-0.5 rounded">Main</div>}
              </div>
            ))}
          </div>
          <Pag page={page} pages={pages} set={setPage} />
        </>
      )}
    </div>
  );
}

/* ═══════════ SUBSCRIPTIONS ═══════════ */
function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/api/admin/subscriptions?plan=${filter}&page=${page}`, { withCredentials: true }); setSubs(r.data.subscriptions); setPages(r.data.pages); }
    catch (err) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [filter, page]);
  useEffect(() => { fetch_(); }, [fetch_]);

  return (
    <div data-testid="subscriptions-tab">
      <div className="flex gap-1.5 mb-6">{['all','free','premium','gold'].map(f => <FB key={f} active={filter===f} onClick={() => { setFilter(f); setPage(1); }} testId={`sub-filter-${f}`}><span className="capitalize">{f}</span></FB>)}</div>
      <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
        {loading ? <Spin /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-[#E5E3DB] bg-[#FAF9F6]">
            {['User','Plan','Status','Source','Platform','Expires','Joined'].map(h => <th key={h} className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-[#9C9C97] font-semibold">{h}</th>)}
          </tr></thead><tbody>{subs.map(u => {
            const p = u.subscription?.plan||'free';
            return (<tr key={u.user_id} className="border-b border-[#E5E3DB]/50 hover:bg-[#FAF9F6]">
              <td className="px-5 py-3"><p className="font-medium text-[#2D2D2A]">{u.nickname||u.name||'N/A'}</p><p className="text-xs text-[#9C9C97]">{u.email}</p></td>
              <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase ${PC[p]}`}>{p}</span></td>
              <td className="px-5 py-3 text-xs text-[#6C6C67] capitalize">{u.subscription?.subscription_status||'N/A'}</td>
              <td className="px-5 py-3 text-xs text-[#6C6C67] capitalize">{u.subscription_source||u.subscription?.provider||u.subscription?.source||'braintree'}</td>
              <td className="px-5 py-3 text-xs text-[#6C6C67] capitalize">{u.signup_platform||'web'}</td>
              <td className="px-5 py-3 text-xs text-[#9C9C97]">{u.subscription?.expires_at?.split('T')[0]||'N/A'}</td>
              <td className="px-5 py-3 text-xs text-[#9C9C97]">{u.created_at?.split('T')[0]}</td>
            </tr>);
          })}</tbody></table></div>
        )}
        <Pag page={page} pages={pages} set={setPage} />
      </div>
    </div>
  );
}

/* ═══════════ PAYMENTS ═══════════ */
function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: 20 });
      if (filter !== 'all') p.append('status', filter);
      if (search) p.append('search', search);
      const r = await axios.get(`${API_URL}/api/admin/payments?${p}`, { withCredentials: true });
      setPayments(r.data.payments); setPages(r.data.pages); setSummary(r.data.summary || {});
    } catch (err) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [filter, search, page]);
  useEffect(() => { fetch_(); }, [fetch_]);

  return (
    <div data-testid="payments-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { l: 'Total Revenue', v: `\u20AC${summary.total_revenue || 0}`, c: 'text-[#2D6A4F]', b: 'bg-[#2D6A4F]/10', i: DollarSign },
          { l: 'Paid', v: summary.paid_count || 0, c: 'text-[#2D6A4F]', b: 'bg-[#2D6A4F]/10', i: Check },
          { l: 'Failed', v: summary.failed_count || 0, c: 'text-red-500', b: 'bg-red-50', i: AlertTriangle },
          { l: 'Refunded', v: summary.refunded_count || 0, c: 'text-amber-600', b: 'bg-amber-50', i: RefreshCw },
        ].map(k => (
          <div key={k.l} className="bg-white rounded-2xl border border-[#E5E3DB] p-4">
            <div className="flex items-center gap-2 mb-2"><div className={`w-7 h-7 rounded-lg ${k.b} flex items-center justify-center`}><k.i className={`w-3.5 h-3.5 ${k.c}`} /></div><span className="text-[10px] uppercase tracking-wider text-[#9C9C97] font-medium">{k.l}</span></div>
            <p className="text-xl font-serif font-medium text-[#2D2D2A]">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E3DB] mb-6 p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9C97]" />
          <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by email, transaction ID..."
            className="w-full pl-10 pr-4 py-2.5 border border-[#E5E3DB] rounded-xl text-sm focus:outline-none focus:border-[#6E1A24]" data-testid="payments-search" /></div>
        <div className="flex gap-1.5">{['all','paid','initiated','expired'].map(f => <FB key={f} active={filter===f} onClick={() => { setFilter(f); setPage(1); }}><span className="capitalize">{f}</span></FB>)}</div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
        {loading ? <Spin /> : payments.length === 0 ? <div className="p-12 text-center text-[#9C9C97]">No payments found</div> : (
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-[#E5E3DB] bg-[#FAF9F6]">
            {['Transaction','User','Plan','Amount','Status','Date'].map(h => <th key={h} className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-[#9C9C97] font-semibold">{h}</th>)}
          </tr></thead><tbody>{payments.map(p => (
            <tr key={p.transaction_id || p.session_id} className="border-b border-[#E5E3DB]/50 hover:bg-[#FAF9F6]">
              <td className="px-5 py-3 text-xs font-mono text-[#6C6C67]">{p.transaction_id || p.session_id?.slice(0, 20)}</td>
              <td className="px-5 py-3 text-xs text-[#2D2D2A]">{p.user_email}</td>
              <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${PC[p.plan]||PC.free}`}>{p.plan}</span></td>
              <td className="px-5 py-3 text-xs font-medium text-[#2D2D2A]">{p.display_price ? `\u20AC${p.display_price}` : 'N/A'}</td>
              <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.payment_status==='paid'?'bg-[#2D6A4F]/10 text-[#2D6A4F]':p.payment_status==='failed'?'bg-red-100 text-red-600':'bg-[#F5F2EB] text-[#6C6C67]'}`}>{p.payment_status}</span></td>
              <td className="px-5 py-3 text-xs text-[#9C9C97]">{p.created_at?.split('T')[0]}</td>
            </tr>))}</tbody></table></div>
        )}
        <Pag page={page} pages={pages} set={setPage} />
      </div>
    </div>
  );
}

/* ═══════════ REPORTS ═══════════ */
function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ status: filter });
      if (search) p.append('search', search);
      const r = await axios.get(`${API_URL}/api/admin/reports?${p}`, { withCredentials: true }); setReports(r.data.reports);
    } catch (err) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [filter, search]);
  useEffect(() => { fetch_(); }, [fetch_]);

  const resolve = async (id, action) => {
    try { await axios.post(`${API_URL}/api/admin/reports/${id}/resolve?action=${action}`, {}, { withCredentials: true }); toast.success('Resolved'); fetch_(); }
    catch (err) { toast.error('Failed'); }
  };

  return (
    <div data-testid="reports-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-1.5">{['pending','resolved','all'].map(f => <FB key={f} active={filter===f} onClick={() => setFilter(f)} testId={`report-filter-${f}`}><span className="capitalize">{f}</span></FB>)}</div>
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9C97]" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by user..."
            className="w-full pl-10 pr-4 py-2.5 border border-[#E5E3DB] rounded-xl text-sm focus:outline-none focus:border-[#6E1A24]" /></div>
      </div>
      {loading ? <Spin /> : reports.length === 0 ? <Empty icon={Flag} text={filter==='pending'?'No pending reports':'No reports found'} /> : (
        <div className="space-y-3">{reports.map(r => (
          <div key={r.report_id} className="bg-white rounded-2xl border border-[#E5E3DB] p-5" data-testid={`report-${r.report_id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${r.status==='pending'?'bg-amber-100 text-amber-700':'bg-[#2D6A4F]/10 text-[#2D6A4F]'}`}>{r.status}</span>
                  <span className="text-xs text-[#9C9C97]">{r.created_at?.split('T')[0]}</span>
                  {r.previous_actions_count > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-500">{r.previous_actions_count} prior actions</span>}
                </div>
                <p className="text-sm text-[#2D2D2A] font-medium mb-1">{r.reason}</p>
                {r.details && <p className="text-xs text-[#6C6C67] mb-2">{r.details}</p>}
                <div className="flex items-center gap-4 text-xs text-[#9C9C97]">
                  <span>Reporter: <strong className="text-[#2D2D2A]">{r.reporter?.name || r.reporter?.nickname || r.reporter_id}</strong></span>
                  <span>Reported: <strong className="text-[#2D2D2A]">{r.reported_user?.name || r.reported_user?.nickname || r.reported_user_id}</strong></span>
                </div>
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-1.5 ml-4 flex-shrink-0">
                  <button onClick={() => resolve(r.report_id, 'dismiss')} className="px-3 py-1.5 rounded-lg bg-[#F5F2EB] text-[#6C6C67] text-xs font-medium hover:bg-[#E5E3DB]" data-testid={`report-dismiss-${r.report_id}`}>Dismiss</button>
                  <button onClick={() => resolve(r.report_id, 'warn')} className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-xs font-medium hover:bg-amber-200">Warn</button>
                  <button onClick={() => resolve(r.report_id, 'suspend')} className="px-3 py-1.5 rounded-lg bg-red-100 text-red-600 text-xs font-medium hover:bg-red-200" data-testid={`report-suspend-${r.report_id}`}>Suspend</button>
                </div>
              )}
            </div>
          </div>
        ))}</div>
      )}
    </div>
  );
}

/* ═══════════ ACTION LOGS ═══════════ */
function ActionLogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: 30 });
      if (typeFilter) p.append('action_type', typeFilter);
      const r = await axios.get(`${API_URL}/api/admin/action-logs?${p}`, { withCredentials: true });
      setLogs(r.data.logs); setPages(r.data.pages); setTotal(r.data.total);
    } catch (err) { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [page, typeFilter]);
  useEffect(() => { fetch_(); }, [fetch_]);

  return (
    <div data-testid="action-logs-tab">
      <div className="bg-white rounded-2xl border border-[#E5E3DB] mb-6 p-4 flex flex-col sm:flex-row gap-3 items-center">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9C97]" />
          <input type="text" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} placeholder="Filter by action type (e.g. verify, suspend)..."
            className="w-full pl-10 pr-4 py-2.5 border border-[#E5E3DB] rounded-xl text-sm focus:outline-none focus:border-[#6E1A24]" data-testid="logs-search" /></div>
        <span className="text-xs text-[#9C9C97]">{total} total logs</span>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
        {loading ? <Spin /> : logs.length === 0 ? <div className="p-12 text-center text-[#9C9C97]">No action logs yet</div> : (
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-[#E5E3DB] bg-[#FAF9F6]">
            {['Action','Admin','Target User','Note','Date'].map(h => <th key={h} className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-[#9C9C97] font-semibold">{h}</th>)}
          </tr></thead><tbody>{logs.map(l => (
            <tr key={l.log_id} className="border-b border-[#E5E3DB]/50 hover:bg-[#FAF9F6]">
              <td className="px-5 py-3"><span className="text-xs font-medium text-[#2D2D2A] capitalize bg-[#F5F2EB] px-2 py-0.5 rounded">{l.action_type?.replace(/_/g, ' ')}</span></td>
              <td className="px-5 py-3 text-xs text-[#6C6C67]">{l.admin_email}</td>
              <td className="px-5 py-3 text-xs font-mono text-[#6C6C67]">{l.target_user_id || 'N/A'}</td>
              <td className="px-5 py-3 text-xs text-[#9C9C97] max-w-[200px] truncate">{l.note || '-'}</td>
              <td className="px-5 py-3 text-xs text-[#9C9C97]">{l.created_at?.replace('T', ' ').slice(0, 19)}</td>
            </tr>))}</tbody></table></div>
        )}
        <Pag page={page} pages={pages} set={setPage} />
      </div>
    </div>
  );
}

/* ═══════════ SETTINGS ═══════════ */
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Change password state
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/settings`, { withCredentials: true })
      .then(r => setSettings(r.data))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API_URL}/api/admin/settings`, settings, { withCredentials: true }); toast.success('Settings saved'); }
    catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!currentPw || !newPw || !confirmPw) { toast.error('All fields required'); return; }
    if (newPw !== confirmPw) { toast.error('New passwords do not match'); return; }
    if (newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setPwSaving(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: currentPw, new_password: newPw, confirm_password: confirmPw,
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally { setPwSaving(false); }
  };

  if (loading) return <Spin />;
  if (!settings) return <Empty icon={SettingsIcon} text="No settings" />;

  const Num = ({ label, field }) => (
    <div className="flex items-center justify-between py-3 border-b border-[#E5E3DB]/50">
      <span className="text-sm text-[#2D2D2A]">{label}</span>
      <input type="number" value={settings[field] ?? ''} onChange={e => setSettings(s => ({ ...s, [field]: parseInt(e.target.value) || 0 }))}
        className="w-24 px-3 py-1.5 border border-[#E5E3DB] rounded-lg text-sm text-right focus:outline-none focus:border-[#6E1A24]" />
    </div>
  );
  const Toggle = ({ label, field }) => (
    <div className="flex items-center justify-between py-3 border-b border-[#E5E3DB]/50">
      <span className="text-sm text-[#2D2D2A]">{label}</span>
      <button onClick={() => setSettings(s => ({ ...s, [field]: !s[field] }))}
        className={`w-10 h-5 rounded-full transition-colors ${settings[field] ? 'bg-[#2D6A4F]' : 'bg-[#E5E3DB]'}`}>
        <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${settings[field] ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-tab">
      <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-4">Limits & Thresholds</h3>
        <Num label="Free Daily Likes Limit" field="free_daily_likes" />
        <Num label="Auto-Suspend After Reports" field="moderation_auto_suspend_reports" />
        <Num label="Referral Reward Days" field="referral_reward_days" />
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-4">Feature Flags</h3>
        <Toggle label="Premium: See Who Liked You" field="premium_see_likes" />
        <Toggle label="Premium: Unlimited Likes" field="premium_unlimited_likes" />
        <Toggle label="Gold: Profile Boost" field="gold_boost" />
        <Toggle label="Gold: Priority Visibility" field="gold_priority" />
        <Toggle label="Onboarding Photo Required" field="onboarding_photo_required" />
        <Toggle label="Referral System Enabled" field="referral_enabled" />
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#6E1A24] text-white rounded-xl px-6 py-3 font-semibold flex items-center gap-2" data-testid="save-settings-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
      </Button>

      {/* Change Admin Password */}
      <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6" data-testid="admin-change-password">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-4">Change Admin Password</h3>
        <form onSubmit={changePassword} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[#9C9C97] mb-1">Current Password</label>
            <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required
              className="w-full px-4 py-2.5 border border-[#E5E3DB] rounded-xl text-sm focus:outline-none focus:border-[#6E1A24]"
              placeholder="Enter current password" data-testid="admin-current-pw" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#9C9C97] mb-1">New Password</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={8}
              className="w-full px-4 py-2.5 border border-[#E5E3DB] rounded-xl text-sm focus:outline-none focus:border-[#6E1A24]"
              placeholder="Min 8 characters" data-testid="admin-new-pw" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#9C9C97] mb-1">Confirm New Password</label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required minLength={8}
              className="w-full px-4 py-2.5 border border-[#E5E3DB] rounded-xl text-sm focus:outline-none focus:border-[#6E1A24]"
              placeholder="Repeat new password" data-testid="admin-confirm-pw" />
          </div>
          <Button type="submit" disabled={pwSaving}
            className="bg-[#6E1A24] text-white rounded-xl px-6 py-2.5 font-semibold text-sm flex items-center gap-2 mt-2"
            data-testid="admin-change-pw-btn">
            {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Change Password
          </Button>
        </form>
      </div>
    </div>
  );
}
